import { useState } from 'react';
import { useStore } from '@/state/store';
import { solveMaxDraw, solveMaxInitialBorrow, solveRequiredReturn, type SolverMode, type SolverResult } from '@/engine/solver';
import { fmtCurrency } from '@/lib/format';

/** Goal-seek solver (§6.16, §10.7). */
export function SolverPanel() {
  const inputs = useStore((s) => s.inputs);
  const setInputs = useStore((s) => s.setInputs);
  const [mode, setMode] = useState<SolverMode>('max_draw');
  const [targetLtv, setTargetLtv] = useState(0.85);
  const [targetNetWorth, setTargetNetWorth] = useState(1_500_000);
  const [result, setResult] = useState<SolverResult | null>(null);

  const run = () => {
    if (mode === 'max_draw') setResult(solveMaxDraw(inputs, targetLtv));
    else if (mode === 'max_initial_borrow') setResult(solveMaxInitialBorrow(inputs, targetLtv));
    else setResult(solveRequiredReturn(inputs, targetNetWorth));
  };

  const apply = () => {
    if (!result) return;
    if (result.mode === 'max_draw') setInputs({ drawAmount: Math.floor(result.value) });
    else if (result.mode === 'max_initial_borrow') setInputs({ initialBalance: Math.floor(result.value) });
    else setInputs({ portfolioExpectedReturnAnnualPct: +result.value.toFixed(2) });
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <h3 className="mb-2 text-sm font-semibold">Goal-seek solver</h3>
      <div className="flex flex-col gap-2 text-sm">
        <select className="rounded border border-slate-300 px-2 py-1 dark:border-slate-600 dark:bg-slate-900" value={mode} onChange={(e) => setMode(e.target.value as SolverMode)}>
          <option value="max_draw">Max sustainable monthly draw</option>
          <option value="max_initial_borrow">Max initial borrow</option>
          <option value="required_return">Required portfolio return</option>
        </select>
        {mode === 'required_return' ? (
          <label className="flex items-center justify-between gap-2 text-xs">Target terminal net worth
            <input type="number" className="w-32 rounded border border-slate-300 px-2 py-1 dark:border-slate-600 dark:bg-slate-900" value={targetNetWorth} onChange={(e) => setTargetNetWorth(Number(e.target.value))} />
          </label>
        ) : (
          <label className="flex items-center justify-between gap-2 text-xs">Target worst-case LTV
            <input type="number" step={0.05} className="w-24 rounded border border-slate-300 px-2 py-1 dark:border-slate-600 dark:bg-slate-900" value={targetLtv} onChange={(e) => setTargetLtv(Number(e.target.value))} />
          </label>
        )}
        <button type="button" className="self-start rounded bg-sky-600 px-3 py-1 text-white" onClick={run}>Solve</button>
        {result && (
          <div className="rounded bg-slate-50 p-2 text-xs dark:bg-slate-900/50">
            <div className="text-base font-semibold tabular-nums">
              {result.mode === 'required_return' ? `${result.value.toFixed(2)}%` : fmtCurrency(result.value)}
            </div>
            <div className="text-slate-500">Binding constraint: {result.bindingConstraint}</div>
            <button type="button" className="mt-1 rounded border border-slate-300 px-2 py-0.5 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800" onClick={apply}>Apply to inputs</button>
          </div>
        )}
      </div>
    </div>
  );
}
